import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { runReadinessAssessment } from '../../../api/educentialApi';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Target,
  TrendingUp,
  ChevronRight,
  Award,
  BookOpen,
  FileCheck,
  Sparkles
} from 'lucide-react';

const CRITERIA = [
  {
    id: 'academicQualification',
    label: 'Do you have the required academic qualification?',
    subtitle: "Degree, diploma, or equivalent that meets UK university requirements",
    icon: Award,
    weight: 20
  },
  {
    id: 'englishProficiency',
    label: 'Do you have English language proficiency proof?',
    subtitle: 'IELTS, TOEFL, PTE, or Medium of Instruction letter',
    icon: BookOpen,
    weight: 20
  },
  {
    id: 'financialCapacity',
    label: 'Can you demonstrate financial capacity?',
    subtitle: 'Proof of funds for tuition and living costs',
    icon: Target,
    weight: 20
  },
  {
    id: 'clearStudyPlan',
    label: 'Do you have a clear study plan?',
    subtitle: 'Know what you want to study and why',
    icon: FileCheck,
    weight: 15
  },
  {
    id: 'documentsReady',
    label: 'Are your documents ready?',
    subtitle: 'Passport, transcripts, certificates, references',
    icon: FileCheck,
    weight: 15
  },
  {
    id: 'timelineAwareness',
    label: 'Are you aware of application timelines?',
    subtitle: 'Application deadlines and visa processing times',
    icon: TrendingUp,
    weight: 10
  }
];

export default function ReadinessAssessment() {
  const [responses, setResponses] = useState({});
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleResponse = (criteriaId, value) => {
    setResponses(prev => ({ ...prev, [criteriaId]: value }));
    setError('');
  };

  const allAnswered = CRITERIA.every(c => responses[c.id] !== undefined);

  const handleSubmit = async () => {
    if (!allAnswered) {
      setError('Please answer all questions to get your readiness assessment');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await runReadinessAssessment('UK', responses);
      setResult(response);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Readiness assessment error:', err);
      setError(err.response?.data?.error || 'Failed to assess readiness. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (result) {
    return <ResultsScreen result={result} responses={responses} onStartOver={() => { setResult(null); setResponses({}); }} />;
  }

  return (
    <div className="bg-gray-50 min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-3">
              <Target className="w-6 h-6 text-yellow-500" />
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                UK Study Readiness Assessment
              </h1>
            </div>
            <p className="text-gray-600 text-lg">
              Evaluate your preparedness to study in the UK
            </p>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                {Object.keys(responses).length} of {CRITERIA.length} questions answered
              </p>
              <div className="h-2 bg-gray-50 rounded-full mt-2 w-64">
                <div
                  className="h-full bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full transition-all duration-300"
                  style={{ width: `${(Object.keys(responses).length / CRITERIA.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assessment Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-8">
          {CRITERIA.map((criteria, index) => {
            const Icon = criteria.icon;
            const response = responses[criteria.id];

            return (
              <div
                key={criteria.id}
                className="bg-white border border-gray-200 shadow-sm rounded-xl p-8 shadow-sm hover:border-yellow-400 hover:shadow-lg transition-all"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-yellow-500/20 border border-yellow-500/30 flex-shrink-0">
                    <Icon className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {index + 1}. {criteria.label}
                      </h3>
                      {response !== undefined && (
                        <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${
                          response ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {response ? (
                            <>
                              <CheckCircle2 className="w-4 h-4" />
                              <span>Yes</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="w-4 h-4" />
                              <span>No</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm">{criteria.subtitle}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleResponse(criteria.id, true)}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                      response === true
                        ? 'border-green-500 bg-green-500/10'
                        : 'border-gray-1000 bg-gray-50 hover:border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <CheckCircle2 className={`w-5 h-5 ${response === true ? 'text-green-500' : 'text-gray-600'}`} />
                      <span className={`font-medium ${response === true ? 'text-green-400' : 'text-gray-300'}`}>
                        Yes, I have this
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => handleResponse(criteria.id, false)}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                      response === false
                        ? 'border-red-500 bg-red-500/10'
                        : 'border-gray-1000 bg-gray-50 hover:border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <XCircle className={`w-5 h-5 ${response === false ? 'text-red-500' : 'text-gray-600'}`} />
                      <span className={`font-medium ${response === false ? 'text-red-400' : 'text-gray-300'}`}>
                        No, not yet
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Submit Button */}
        <div className="mt-12 flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={!allAnswered || isSubmitting}
            className="flex items-center space-x-2 px-12 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-lg transition-all shadow-lg shadow-yellow-500/20"
          >
            <span>{isSubmitting ? 'Assessing...' : 'Get My Readiness Score'}</span>
            {!isSubmitting && <ChevronRight className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResultsScreen({ result, responses, onStartOver }) {
  const navigate = useNavigate();

  const score = result.readinessScore || result.score || 0;
  const isReady = result.ready || result.isReady || false;
  const recommendations = result.recommendations || [];

  const getScoreColor = (score) => {
    if (score >= 80) return { bg: 'bg-green-500/20', border: 'border-green-500', text: 'text-green-500', label: 'Excellent' };
    if (score >= 60) return { bg: 'bg-yellow-500/20', border: 'border-yellow-500', text: 'text-yellow-500', label: 'Good' };
    if (score >= 40) return { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-500', label: 'Fair' };
    return { bg: 'bg-red-500/20', border: 'border-red-500', text: 'text-red-500', label: 'Needs Work' };
  };

  const scoreStyle = getScoreColor(score);

  // Count yes/no responses
  const yesCount = Object.values(responses).filter(r => r === true).length;
  const noCount = Object.values(responses).filter(r => r === false).length;

  return (
    <div className="bg-gray-50 min-h-full">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Score Display */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full ${scoreStyle.bg} border-4 ${scoreStyle.border} mb-6 relative`}>
            <div className="text-center">
              <p className={`text-5xl font-bold ${scoreStyle.text}`}>{score}%</p>
            </div>
            <div className="absolute -top-2 -right-2">
              <Sparkles className={`w-8 h-8 ${scoreStyle.text}`} />
            </div>
          </div>

          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Your Readiness Score
          </h1>
          <p className={`text-2xl font-semibold ${scoreStyle.text} mb-2`}>
            {scoreStyle.label}
          </p>
          <p className="text-gray-600 text-lg">
            {isReady
              ? "You're well-prepared to begin your UK study journey!"
              : "You're on the right track! A few more steps to take."}
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Total Criteria</p>
                <p className="text-3xl font-bold text-gray-900">{CRITERIA.length}</p>
              </div>
              <Target className="w-12 h-12 text-gray-600" />
            </div>
          </div>

          <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-400 text-sm mb-1">Ready</p>
                <p className="text-3xl font-bold text-green-500">{yesCount}</p>
              </div>
              <CheckCircle2 className="w-12 h-12 text-green-500" />
            </div>
          </div>

          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-400 text-sm mb-1">Needs Attention</p>
                <p className="text-3xl font-bold text-red-500">{noCount}</p>
              </div>
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
          </div>
        </div>

        {/* Detailed Assessment */}
        <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
            <FileCheck className="w-6 h-6 text-yellow-500" />
            <span>Detailed Assessment</span>
          </h2>

          <div className="space-y-4">
            {CRITERIA.map((criteria) => {
              const response = responses[criteria.id];
              const Icon = criteria.icon;

              return (
                <div
                  key={criteria.id}
                  className={`flex items-start space-x-4 p-4 rounded-lg border ${
                    response
                      ? 'bg-green-500/5 border-green-500/30'
                      : 'bg-red-500/5 border-red-500/30'
                  }`}
                >
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 ${
                    response ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}>
                    <Icon className={`w-5 h-5 ${response ? 'text-green-500' : 'text-red-500'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="text-gray-900 font-medium">{criteria.label}</h3>
                      {response ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 ml-2" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 ml-2" />
                      )}
                    </div>
                    <p className="text-gray-600 text-sm">{criteria.subtitle}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recommendations */}
        {recommendations && recommendations.length > 0 && (
          <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
              <TrendingUp className="w-6 h-6 text-yellow-500" />
              <span>Next Steps & Recommendations</span>
            </h2>

            <div className="space-y-4">
              {recommendations.map((rec, idx) => (
                <div key={idx} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-500 text-black font-bold text-sm flex-shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-300 leading-relaxed">{rec}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
          {isReady && (
            <button
              onClick={() => navigate('/overseas/application')}
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold rounded-lg transition-all shadow-lg shadow-yellow-500/20 flex items-center justify-center space-x-2"
            >
              <span>Start Application</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          )}

          <button
            onClick={() => navigate('/overseas/uk/pathfinder')}
            className="w-full sm:w-auto px-8 py-4 bg-gray-50 hover:bg-gray-500 text-gray-900 font-semibold rounded-lg transition-all border border-gray-200"
          >
            Get University Recommendations
          </button>

          <button
            onClick={onStartOver}
            className="w-full sm:w-auto px-8 py-4 bg-gray-50 hover:bg-gray-50 text-gray-900 rounded-lg transition-all border-2 border-gray-200"
          >
            Retake Assessment
          </button>
        </div>
      </div>
    </div>
  );
}
